/**
 * Field metadata for the filter builder.
 *
 * `sviw_WebSiteCMS_FilterBuilder_ViewFields` normalizes the SQL data types into
 * the handful of types the builder groups fields by, so the mapping here is
 * mostly a rename. The loading lives in `useFilterFields` in
 * `@olenbetong/appframe-react`.
 */

import type { FieldMetadata, FilterFieldType } from "./types.js";

/** A raw `sviw_WebSiteCMS_FilterBuilder_ViewFields` record. */
export type ViewFieldRecord = {
	FieldName?: string | null;
	/** Already normalized by the view; anything unexpected is treated as text. */
	DataType?: string | null;
	DBObjectID?: string | null;
	Nullable?: boolean | number | null;
	ExcludeFromFilter?: boolean | number | null;
	ValueListRecordSource?: string | null;
};

const FIELD_TYPES: FilterFieldType[] = ["string", "number", "boolean", "date", "datetime", "uniqueidentifier"];

/** The builder type for a normalized `DataType`. Unknown types are filtered as text. */
export function toFilterFieldType(dataType: string | null | undefined): FilterFieldType {
	let normalized = dataType?.trim().toLowerCase();

	return FIELD_TYPES.find((type) => type === normalized) ?? "string";
}

function toBoolean(value: boolean | number | null | undefined) {
	return value === true || value === 1;
}

/** Maps a view field record onto the metadata the builder renders. */
export function toFieldMetadata(record: ViewFieldRecord): FieldMetadata {
	return {
		name: record.FieldName ?? "",
		type: toFilterFieldType(record.DataType),
		nullable: toBoolean(record.Nullable),
		excludeFromFilter: toBoolean(record.ExcludeFromFilter),
		valueListRecordSource: record.ValueListRecordSource || undefined,
	};
}

/**
 * Maps and sorts view field records. Fields the view marks as excluded are
 * dropped rather than hidden, since nothing else can use them.
 */
export function toFieldMetadataList(records: ViewFieldRecord[]): FieldMetadata[] {
	return records
		.map(toFieldMetadata)
		.filter((field) => field.name !== "" && !field.excludeFromFilter)
		.sort((a, b) => a.name.localeCompare(b.name));
}
